import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { ThreeMFLoader } from 'three/addons/loaders/3MFLoader.js';

function webglAvailable() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
      (c.getContext('webgl') || c.getContext('experimental-webgl')));
  } catch (e) { return false; }
}

class Order3MFViewer {
  constructor({ containerId, modelUrl, downloadUrl, filamentColors }) {
    this.container = document.getElementById(containerId);
    this.filamentColors = filamentColors || [];
    this.modelUrl   = modelUrl;
    this.downloadUrl = downloadUrl;
    this.autoRotating = false;
    this.rafId        = null;
    this.initialCamPos = null;

    if (!this.container) return;

    if (!webglAvailable()) {
      this.showNoWebGL();
      return;
    }

    this.init();
    this.loadModel();
    this.setupResize();
  }

  init() {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf5ebe2);

    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100000);
    this.camera.position.set(0, 200, 400);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.container.appendChild(this.renderer.domElement);

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const d1 = new THREE.DirectionalLight(0xffffff, 0.9);
    d1.position.set(1, 2, 2);
    this.scene.add(d1);
    const d2 = new THREE.DirectionalLight(0xfff8f0, 0.4);
    d2.position.set(-2, -1, -1);
    this.scene.add(d2);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping  = true;
    this.controls.dampingFactor  = 0.05;
    this.controls.minDistance    = 10;
    this.controls.maxDistance    = 50000;
    this.controls.autoRotateSpeed = 1.5;

    this.buildUI();
    this.animate();
  }

  buildUI() {
    this.loadingEl = this.container.querySelector('.viewer-loading');

    const btns = document.createElement('div');
    btns.className = 'viewer-buttons';

    const reset = this.mkBtn('↺', 'Resetear vista', () => this.resetCamera());
    this.rotateBtn = this.mkBtn('⟳', 'Auto-rotación', () => this.toggleAutoRotate());
    const fs = this.mkBtn('⛶', 'Pantalla completa', () => this.toggleFullscreen());

    const dl = document.createElement('a');
    dl.className = 'viewer-btn';
    dl.href = this.downloadUrl;
    dl.setAttribute('download', '');
    dl.title = 'Descargar .3mf';
    dl.textContent = '⬇';

    btns.append(reset, this.rotateBtn, fs, dl);
    this.container.appendChild(btns);
  }

  mkBtn(icon, title, handler) {
    const b = document.createElement('button');
    b.className = 'viewer-btn';
    b.title     = title;
    b.textContent = icon;
    b.addEventListener('click', handler);
    return b;
  }

  loadModel() {
    const loader = new ThreeMFLoader();
    loader.load(
      this.modelUrl,
      (object) => {
        object.rotation.x = -Math.PI / 2;

        const box    = new THREE.Box3().setFromObject(object);
        const center = box.getCenter(new THREE.Vector3());
        object.position.sub(center);
        this.scene.add(object);

        const size   = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov    = this.camera.fov * (Math.PI / 180);
        const dist   = (maxDim / 2 / Math.tan(fov / 2)) * 1.8;

        this.camera.position.set(dist, dist * 0.6, dist);
        this.camera.lookAt(0, 0, 0);
        this.controls.target.set(0, 0, 0);
        this.controls.minDistance = maxDim * 0.2;
        this.controls.maxDistance = maxDim * 15;
        this.controls.update();

        this.initialCamPos = this.camera.position.clone();

        if (this.loadingEl) this.loadingEl.style.display = 'none';

        const layers = this.collectLayers(object);
        this.layers = layers;

        const DEFAULT_COLORS = {
          'Base':  '#1B3A6B',
          'Agua':  '#34BBCC',
          'Vías':  '#F0EDE8',
          'Ruta':  '#FF6B00',
        };
        layers.forEach((data, label) => {
          const hex = DEFAULT_COLORS[label];
          if (hex) {
            data.color = hex;
            data.meshes.forEach(m => m.material.color.set(hex));
          }
        });

        if (layers.size > 0) this.buildColorPanel(layers);
        this.checkMultiColor();
      },
      (xhr) => {
        if (this.loadingEl && xhr.total) {
          const pct = Math.round((xhr.loaded / xhr.total) * 100);
          this.loadingEl.textContent = `Cargando modelo… ${pct}%`;
        }
      },
      () => {
        this.showError('No se pudo cargar el modelo 3D.');
      }
    );
  }

  collectLayers(object) {
    const LABEL_MAP = {
      route: 'Ruta', ruta: 'Ruta', track: 'Ruta', camino: 'Ruta',
      water: 'Agua', agua: 'Agua', lago: 'Agua', lake: 'Agua', river: 'Agua',
      road: 'Vías', vial: 'Vías', street: 'Vías', calle: 'Vías',
      terrain: 'Terreno', terreno: 'Terreno', topo: 'Terreno', relief: 'Terreno',
      base: 'Base', disc: 'Base', plate: 'Base', fondo: 'Base',
    };
    const layers = new Map();
    let counter = 1;
    object.traverse(child => {
      if (!child.isMesh) return;
      const raw = (child.name || child.parent?.name || '').toLowerCase();
      let label = Object.keys(LABEL_MAP).find(k => raw.includes(k));
      label = label ? LABEL_MAP[label] : (child.name || `Capa ${counter++}`);
      if (!layers.has(label)) {
        const color = '#' + (child.material?.color?.getHexString() || 'aaaaaa');
        layers.set(label, { meshes: [], color });
      }
      child.material = child.material.clone();
      layers.get(label).meshes.push(child);
    });
    return layers;
  }

  buildColorPanel(layers) {
    const palette = this.filamentColors;
    if (!palette.length) return;

    const panel = document.createElement('div');
    panel.className = 'viewer-color-panel';
    panel.innerHTML = `<div class="color-panel-head">
      <span class="eyebrow"><span class="dot"></span>COLOR DE FILAMENTO POR CAPA</span>
    </div>`;

    const controls = document.createElement('div');
    controls.className = 'color-rows';

    layers.forEach((data, label) => {
      const row = document.createElement('div');
      row.className = 'color-row';

      const lbl = document.createElement('span');
      lbl.className = 'color-row-label';
      lbl.textContent = label;

      const swatchGrid = document.createElement('div');
      swatchGrid.className = 'filament-swatches';

      palette.forEach(filament => {
        const sw = document.createElement('button');
        sw.className = 'filament-swatch';
        sw.title = filament.name;
        sw.style.background = filament.hex;
        sw.type = 'button';
        if (filament.hex.toLowerCase() === data.color.toLowerCase()) {
          sw.classList.add('is-selected');
        }
        sw.addEventListener('click', () => {
          swatchGrid.querySelectorAll('.filament-swatch').forEach(s => s.classList.remove('is-selected'));
          sw.classList.add('is-selected');
          data.color = filament.hex;
          data.meshes.forEach(m => m.material.color.set(filament.hex));
          this.checkMultiColor();
        });
        swatchGrid.appendChild(sw);
      });

      row.append(lbl, swatchGrid);
      controls.appendChild(row);
    });

    panel.appendChild(controls);
    this.container.insertAdjacentElement('afterend', panel);
  }

  checkMultiColor() {
    if (!this.layers) return;
    const unique = new Set(Array.from(this.layers.values()).map(d => d.color)).size;
    const layerColors = {};
    this.layers.forEach((data, label) => { layerColors[label] = data.color; });
    this.container.dispatchEvent(new CustomEvent('kudos3d:colorchange', {
      bubbles: true,
      detail: { uniqueColors: unique, layerColors }
    }));
  }

  resetCamera() {
    if (!this.initialCamPos) return;
    this.camera.position.copy(this.initialCamPos);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  toggleAutoRotate() {
    this.autoRotating = !this.autoRotating;
    this.controls.autoRotate = this.autoRotating;
    this.rotateBtn.classList.toggle('is-active', this.autoRotating);
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      (this.container.requestFullscreen || this.container.webkitRequestFullscreen)
        .call(this.container);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    }
  }

  animate() {
    this.rafId = requestAnimationFrame(() => this.animate());
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  setupResize() {
    this.ro = new ResizeObserver(() => {
      const w = this.container.clientWidth;
      const h = this.container.clientHeight;
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h);
    });
    this.ro.observe(this.container);
  }

  showNoWebGL() {
    const el = this.container.querySelector('.viewer-loading');
    if (!el) return;
    el.className = 'viewer-error';
    el.innerHTML = `Tu navegador no soporta WebGL.
      <a href="${this.downloadUrl}" download>Descarga el archivo .3mf</a>
      para verlo en tu computadora.`;
  }

  showError(msg) {
    if (!this.loadingEl) return;
    this.loadingEl.className = 'viewer-error';
    this.loadingEl.innerHTML =
      `${msg} <a href="${this.downloadUrl}" download>Descargar archivo</a>`;
  }

  destroy() {
    cancelAnimationFrame(this.rafId);
    this.ro?.disconnect();
    this.scene.traverse(obj => {
      obj.geometry?.dispose();
      if (obj.material) {
        (Array.isArray(obj.material) ? obj.material : [obj.material])
          .forEach(m => m.dispose());
      }
    });
    this.renderer.dispose();
  }
}

const el = document.getElementById('visor-3d');
if (el?.dataset.modelUrl) {
  let filamentColors = [];
  try { filamentColors = JSON.parse(el.dataset.filamentColors || '[]'); } catch (_) {}
  new Order3MFViewer({
    containerId:    'visor-3d',
    modelUrl:       el.dataset.modelUrl,
    downloadUrl:    el.dataset.modelUrl,
    filamentColors,
  });
}
